// 증분 SHA-256 — 파일 전송(설계서 §9.4) 무결성 확인용.
//
// 왜 직접 구현하나: 브라우저 WebCrypto(`crypto.subtle.digest`)는 **한 번에 전체 버퍼**만 받는다.
// 10 GiB 파일을 메모리에 올릴 수 없으므로 조각을 읽는 대로 먹이는 증분 해시가 필요하다(FIPS 180-4 그대로).
// 에이전트(ring)·Android(MessageDigest) 결과와 같아야 한다 — Node 시험에서 node:crypto 와 대조한다.

const K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

export class Sha256 {
  private readonly h = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ]);
  private readonly w = new Uint32Array(64);
  /** 64바이트에 못 미친 나머지. */
  private readonly block = new Uint8Array(64);
  private blockLen = 0;
  /** 먹인 전체 바이트(10 GiB 도 Number 로 정확 — 2^53 미만). */
  private length = 0;
  private finished = false;

  update(data: Uint8Array): this {
    if (this.finished) {
      throw new Error('Sha256: 이미 끝났다');
    }
    this.length += data.length;
    let off = 0;
    if (this.blockLen > 0) {
      const take = Math.min(64 - this.blockLen, data.length);
      this.block.set(data.subarray(0, take), this.blockLen);
      this.blockLen += take;
      off = take;
      if (this.blockLen < 64) {
        return this;
      }
      this.compress(this.block, 0);
      this.blockLen = 0;
    }
    for (; off + 64 <= data.length; off += 64) {
      this.compress(data, off);
    }
    if (off < data.length) {
      this.block.set(data.subarray(off), 0);
      this.blockLen = data.length - off;
    }
    return this;
  }

  /** 끝내고 소문자 16진수 64자. */
  digestHex(): string {
    if (this.finished) {
      throw new Error('Sha256: 이미 끝났다');
    }
    const bits = this.length * 8;
    const pad = new Uint8Array((this.blockLen < 56 ? 56 : 120) - this.blockLen + 8);
    pad[0] = 0x80;
    const dv = new DataView(pad.buffer);
    // 길이(비트)를 빅엔디언 u64 로 — 상위 32비트는 2^32 로 나눈 몫.
    dv.setUint32(pad.length - 8, Math.floor(bits / 2 ** 32), false);
    dv.setUint32(pad.length - 4, bits >>> 0, false);
    this.length -= pad.length; // update 가 더할 패딩 길이를 상쇄
    this.update(pad);
    this.finished = true;
    let hex = '';
    for (const v of this.h) {
      hex += v.toString(16).padStart(8, '0');
    }
    return hex;
  }

  private compress(buf: Uint8Array, off: number): void {
    const w = this.w;
    for (let t = 0; t < 16; t++) {
      const p = off + t * 4;
      w[t] = (buf[p] << 24) | (buf[p + 1] << 16) | (buf[p + 2] << 8) | buf[p + 3];
    }
    for (let t = 16; t < 64; t++) {
      const x = w[t - 15];
      const y = w[t - 2];
      const s0 = ((x >>> 7) | (x << 25)) ^ ((x >>> 18) | (x << 14)) ^ (x >>> 3);
      const s1 = ((y >>> 17) | (y << 15)) ^ ((y >>> 19) | (y << 13)) ^ (y >>> 10);
      w[t] = (w[t - 16] + s0 + w[t - 7] + s1) | 0;
    }
    let [a, b, c, d, e, f, g, h] = this.h;
    for (let t = 0; t < 64; t++) {
      const S1 = ((e >>> 6) | (e << 26)) ^ ((e >>> 11) | (e << 21)) ^ ((e >>> 25) | (e << 7));
      const ch = (e & f) ^ (~e & g);
      const t1 = (h + S1 + ch + K[t] + w[t]) | 0;
      const S0 = ((a >>> 2) | (a << 30)) ^ ((a >>> 13) | (a << 19)) ^ ((a >>> 22) | (a << 10));
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (S0 + maj) | 0;
      h = g;
      g = f;
      f = e;
      e = (d + t1) | 0;
      d = c;
      c = b;
      b = a;
      a = (t1 + t2) | 0;
    }
    this.h[0] += a;
    this.h[1] += b;
    this.h[2] += c;
    this.h[3] += d;
    this.h[4] += e;
    this.h[5] += f;
    this.h[6] += g;
    this.h[7] += h;
  }
}

/** 한 번에 해시(작은 버퍼·시험용). */
export function sha256Hex(data: Uint8Array): string {
  return new Sha256().update(data).digestHex();
}
